/**
 * ============================================================
 * OTP VERIFICATION MODAL COMPONENT
 * ============================================================
 * 
 * Reusable OTP verification modal that can be used across the app.
 * 
 * Props:
 * - open: boolean - Controls modal visibility
 * - onClose: function - Called when modal is closed
 * - email: string - Email to send/verify OTP
 * - otpType: string - Type of OTP (e.g., "signup")
 * - onSuccess: function - Called after successful verification
 * ============================================================
 */

import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Input, Button, Typography, Flex, Alert } from "antd";

import { VALIDATION_MESSAGES } from "../../utils/constants";
import { config } from "../../config";
import CaptchaWidget from "./CaptchaWidget";
import {
    sendOtp,
    selectSendOtpSuccess,
    selectSendOtpLoading,
    resetSendOtpSuccess,
    verifyOtp,
    selectVerifyOtpSuccess,
    selectVerifyOtpError,
    selectVerifyOtpLoading,
    resetVerifyOtpSuccess
} from "../../redux/userSlice";

const { Title, Text } = Typography;

// Countdown duration in seconds
const RESEND_COUNTDOWN = 60;

export default function VerifyOtpModal({
    open,
    onClose,
    email,
    otpType = "signup",
    onSuccess
}) {
    const dispatch = useDispatch();

    // Redux state
    const sendOtpSuccess = useSelector(selectSendOtpSuccess);
    const sendOtpLoading = useSelector(selectSendOtpLoading);
    const verifyOtpSuccess = useSelector(selectVerifyOtpSuccess);
    const verifyOtpError = useSelector(selectVerifyOtpError);
    const verifyOtpLoading = useSelector(selectVerifyOtpLoading);

    // Local state
    const [otp, setOtp] = useState(["", "", "", ""]);
    const [otpError, setOtpError] = useState("");
    const [countdown, setCountdown] = useState(RESEND_COUNTDOWN);
    const [canResend, setCanResend] = useState(false);
    const [resendMessage, setResendMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [captchaToken, setCaptchaToken] = useState("");
    const [hasSentForThisOpen, setHasSentForThisOpen] = useState(false);

    const inputRefs = useRef([]);
    const captchaRef = useRef(null);
    const requireCaptcha = !!config.turnstileSiteKey;

    /** When modal opens, allow sending (with or without captcha). */
    useEffect(() => {
        if (open) setHasSentForThisOpen(false);
    }, [open]);

    /** Send OTP when modal opens (no captcha) or when we have token (captcha). */
    useEffect(() => {
        if (!open || !email) return;
        if (!requireCaptcha) {
            dispatch(sendOtp(otpType, email));
        }
    }, [open, email, otpType, dispatch, requireCaptcha]);

    /** When captcha is required and we have a token, send OTP (first time or after resend). */
    useEffect(() => {
        if (requireCaptcha && open && email && captchaToken && !hasSentForThisOpen) {
            dispatch(sendOtp(otpType, email, captchaToken));
            setHasSentForThisOpen(true);
        }
    }, [requireCaptcha, open, email, captchaToken, hasSentForThisOpen, otpType, dispatch]);

    /**
     * HANDLE OTP SENT SUCCESS
     */
    useEffect(() => {
        if (sendOtpSuccess && open) {
            dispatch(resetSendOtpSuccess());
            setTimeout(() => inputRefs.current[0]?.focus(), 100);
            if (countdown < RESEND_COUNTDOWN) {
                setResendMessage("OTP resent successfully!");
                setTimeout(() => setResendMessage(""), 3000);
            }
            setCountdown(RESEND_COUNTDOWN);
            setCanResend(false);
            if (requireCaptcha && captchaRef.current) captchaRef.current.reset();
        }
    }, [sendOtpSuccess, open, dispatch, countdown, requireCaptcha]);

    /**
     * COUNTDOWN TIMER
     */
    useEffect(() => {
        if (open && countdown > 0) {
            const timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        } else if (countdown === 0) {
            setCanResend(true);
        }
    }, [open, countdown]);

    /**
     * HANDLE OTP VERIFICATION SUCCESS
     */
    useEffect(() => {
        if (verifyOtpSuccess) {
            dispatch(resetVerifyOtpSuccess());
            setSuccessMessage("Signup successful!");
            // Call onSuccess callback after delay
            setTimeout(() => {
                resetModalState();
                onSuccess?.();
            }, 2000);
        }
    }, [verifyOtpSuccess, dispatch, onSuccess]);

    /**
     * SHOW OTP ERROR FROM REDUX
     */
    useEffect(() => {
        if (verifyOtpError) {
            // Use validation constant for wrong OTP
            setOtpError(VALIDATION_MESSAGES.OTP_WRONG);
        }
    }, [verifyOtpError]);

    /**
     * RESET MODAL STATE
     */
    const resetModalState = () => {
        setOtp(["", "", "", ""]);
        setOtpError("");
        setCountdown(RESEND_COUNTDOWN);
        setCanResend(false);
        setResendMessage("");
        setSuccessMessage("");
    };

    /**
     * HANDLE MODAL CLOSE
     */
    const handleClose = () => {
        resetModalState();
        onClose?.();
    };

    /**
     * HANDLE RESEND OTP
     */
    const handleResendOtp = () => {
        if (!canResend || sendOtpLoading) return;
        setOtpError("");
        setOtp(["", "", "", ""]);
        if (requireCaptcha) {
            setCaptchaToken("");
            setHasSentForThisOpen(false);
            captchaRef.current?.reset();
        } else {
            dispatch(sendOtp(otpType, email));
        }
    };

    /**
     * FORMAT COUNTDOWN
     */
    const formatCountdown = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    /**
     * HANDLE OTP INPUT CHANGE
     */
    const handleOtpChange = (index, value) => {
        if (value.length > 1) value = value.slice(-1);
        if (value && !/^\d$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        setOtpError("");

        if (value && index < 3) {
            inputRefs.current[index + 1].focus();
        }
    };

    /**
     * HANDLE OTP BACKSPACE
     */
    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    /**
     * HANDLE OTP PASTE
     */
    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").slice(0, 4);
        if (/^\d{1,4}$/.test(pastedData)) {
            const newOtp = pastedData.split("").concat(["", "", "", ""]).slice(0, 4);
            setOtp(newOtp);
            const lastIndex = Math.min(pastedData.length - 1, 3);
            inputRefs.current[lastIndex].focus();
        }
    };

    /**
     * HANDLE OTP VERIFY
     */
    const handleVerifyOtp = () => {
        const otpString = otp.join("");
        if (otpString.length !== 4) {
            setOtpError("Please enter all 4 digits");
            return;
        }
        if (requireCaptcha && !captchaToken) {
            setOtpError("Please complete the verification.");
            return;
        }
        setOtpError("");
        const payload = { otp_type: otpType, otp: otpString, email };
        if (requireCaptcha) payload.captchaToken = captchaToken;
        dispatch(verifyOtp(payload));
    };

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            footer={null}
            centered
            closable={true}
            maskClosable={false}
            width={420}
            styles={{
                mask: {
                    backgroundColor: 'rgba(0, 7, 56, 0.75)',
                    backdropFilter: 'blur(4px)'
                },
                content: {
                    borderRadius: 16,
                    padding: 0,
                    overflow: 'hidden'
                }
            }}
            closeIcon={
                <span
                    className="material-symbols-outlined"
                    style={{
                        fontSize: 24,
                        color: '#666',
                        padding: 8,
                        borderRadius: '50%',
                        background: '#f5f5f5',
                        transition: 'all 0.3s'
                    }}
                >
                    close
                </span>
            }
        >
            <div style={{ padding: '32px 24px', textAlign: 'center' }}>
                {/* Success State */}
                {successMessage ? (
                    <div style={{ padding: '40px 0' }}>
                        <div style={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 24px',
                            animation: 'pulse 1.5s infinite'
                        }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 40, color: '#fff' }}>
                                check_circle
                            </span>
                        </div>
                        <Title level={4} style={{ marginBottom: 8, color: '#52c41a' }}>
                            {successMessage}
                        </Title>
                        <Text className="color-neutral">
                            Redirecting to login page...
                        </Text>
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div style={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px'
                        }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 32, color: '#fff' }}>
                                verified_user
                            </span>
                        </div>
                        <Title level={4} style={{ marginBottom: 8 }}>
                            Verify Your Email
                        </Title>
                        <Text className="color-neutral" style={{ display: 'block', marginBottom: 24 }}>
                            Enter the 4-digit OTP sent to<br />
                            <strong style={{ color: '#2563eb' }}>{email}</strong>
                        </Text>

                        {requireCaptcha && (
                            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
                                <CaptchaWidget ref={captchaRef} onToken={setCaptchaToken} />
                            </div>
                        )}

                        {/* Resend Success Message */}
                        {resendMessage && (
                            <Alert
                                message={resendMessage}
                                type="success"
                                showIcon
                                style={{ marginBottom: 16, textAlign: 'left' }}
                            />
                        )}

                        {/* Error Message */}
                        {otpError && (
                            <Alert
                                message={otpError}
                                type="error"
                                showIcon
                                style={{ marginBottom: 16, textAlign: 'left' }}
                            />
                        )}

                        {/* OTP Input Boxes */}
                        <Flex justify="center" gap={12} style={{ marginBottom: 20, flexDirection: 'row !important' }}>
                            {otp.map((digit, index) => (
                                <Input
                                    key={index}
                                    ref={(el) => (inputRefs.current[index] = el)} 
                                    value={digit}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                    onPaste={index === 0 ? handleOtpPaste : undefined}
                                    maxLength={1}
                                    style={{
                                        width: 56,
                                        height: 56,
                                        textAlign: 'center',
                                        fontSize: 24,
                                        fontWeight: 'bold',
                                        borderRadius: 12,
                                        border: '2px solid #e0e0e0',
                                        transition: 'all 0.3s'
                                    }}
                                />
                            ))}
                        </Flex>

                        {/* Resend OTP Section */}
                        <div style={{ marginBottom: 24 }}>
                            {canResend ? (
                                <Button
                                    type="link"
                                    onClick={handleResendOtp}
                                    loading={sendOtpLoading}
                                    style={{ padding: 0, fontWeight: 500, fontSize: 14 }}
                                >
                                    Resend OTP
                                </Button>
                            ) : (
                                <Text className="color-neutral" style={{ fontSize: 14 }}>
                                    Resend OTP in
                                    <span style={{
                                        fontWeight: 'bold',
                                        color: '#2563eb',
                                        fontFamily: 'monospace',
                                        fontSize: 16
                                    }}>
                                        {formatCountdown(countdown)}
                                    </span>
                                </Text>
                            )}
                        </div>

                        {/* Verify Button */}
                        <Button
                            type="primary"
                            block
                            size="large"
                            className="btnGradient"
                            onClick={handleVerifyOtp}
                            loading={verifyOtpLoading}
                            style={{ height: 48, fontSize: 16, borderRadius: 8 }}
                        >
                            <span className="customizedButtonText">Verify OTP</span>
                        </Button>
                    </>
                )}
            </div>

            {/* Pulse animation */}
            <style>{`
                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                    100% { transform: scale(1); }
                }
            `}</style>
        </Modal>
    );
}
